import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import DropdownMenu from '../dropdownMenu';
import Input from '../input';
import Menu, { MenuItem } from '../menu';

function defaultFilter(value = '', suggestions, max) {
  const inputValue = value.trim().toLowerCase();
  const inputLength = inputValue.length;
  let count = 0;

  return inputLength === 0
    ? []
    : suggestions.filter(suggestion => {
        const keep = count < max && suggestion.toLowerCase().indexOf(inputValue) > -1;

        if (keep) {
          count += 1;
        }

        return keep;
      });
}

export default class AutoComplete extends Component {
  static displayName = 'NeatUI.AutoComplete';

  static propTypes = {
    prefixCls: PropTypes.string,
    inputRef: PropTypes.func,
    /** 备选的建议列表 */
    suggestions: PropTypes.array,
    /** 显示的建议数目最大值 */
    maximumDisplayingSuggestions: PropTypes.number,
    /** 自定义建议项根据输入值的算法 function(value, suggestions, maximumDisplayingSuggestions) */
    filter: PropTypes.func,
    /** 显示建议延迟时间（单位：ms） */
    waitTime: PropTypes.number,
    /** 建议项渲染方法 function(value, suggestion ) => item: ReactNode */
    itemRender: PropTypes.func,
    /** 输入框输入时回调 function(event) */
    onSearch: PropTypes.func,
    /** 选中建议项，或 input 的 value 变化时回调 function(value) */
    onChange: PropTypes.func,
    /** 是否只允许从建议列表里选择 */
    selectOnly: PropTypes.bool,
    /** 建议列表是否和输入框同宽 */
    matchWidth: PropTypes.bool,
    /** 是否高亮关键字 */
    matchHighlight: PropTypes.bool,
    /**
     * 是否占满整行
     */
    fullWidth: PropTypes.bool,
    /** placeholder */
    placeholder: PropTypes.string,
    /**
     * @ignore
     * 菜单缩进距离
     */
    indent: PropTypes.number,
  };

  static defaultProps = {
    selectOnly: false,
    matchWidth: true,
    prefixCls: 'ne-auto-complete',
    suggestions: [],
    filter: defaultFilter,
    maximumDisplayingSuggestions: 5,
    waitTime: 200,
    matchHighlight: true,
    fullWidth: false,
    placeholder: '',
    indent: 6,
  };

  constructor(props) {
    super(props);
    const defaultValue =
      props.defaultValue != null ? props.defaultValue : props.children.props.defaultValue;
    this.state = {
      value: !props.selectOnly || props.suggestions.includes(defaultValue) ? defaultValue : '',
      search: defaultValue,
      matches: [],
      isOpen: false,
    };
  }

  componentWillReceiveProps(newProps) {
    if (newProps.defaultValue !== this.props.defaultValue) {
      this.setState({
        value:
          !newProps.selectOnly || newProps.suggestions.includes(newProps.defaultValue)
            ? newProps.defaultValue
            : '',
        search: newProps.defaultValue,
      });
    }
  }

  width = 187;

  getEl = el => {
    const { inputRef } = this.props;
    this.el = el;

    inputRef && inputRef(el);
  };

  handleOpen = isOpen => {
    const { search } = this.state;
    if (this.el) {
      const { width } = this.el.getBoundingClientRect();
      this.width = width;
    }
    if (isOpen) {
      this.getSuggestions(search);
    } else {
      this.handleBlur();
    }
    this.setState({
      isOpen,
    });
  };

  suggestionsRender(suggestion, value) {
    const { matchHighlight, itemRender } = this.props;
    if (itemRender) {
      return itemRender(value, suggestion);
    }
    if (!matchHighlight) {
      return (
        <MenuItem value={suggestion} role="option" key={suggestion}>
          {suggestion}
        </MenuItem>
      );
    }
    const matches = match(suggestion, value);
    const parts = parse(suggestion, matches);
    return (
      <MenuItem value={suggestion} role="option" key={suggestion}>
        {parts.map((part, index) =>
          part.highlight ? (
            <strong key={String(index)}>{part.text}</strong>
          ) : (
            <span key={String(index)}>{part.text}</span>
          ),
        )}
      </MenuItem>
    );
  }

  getSuggestions = debounce(
    value => {
      const { filter, suggestions, maximumDisplayingSuggestions, selectOnly } = this.props;
      if (selectOnly && !value) {
        return this.setState({
          matches: suggestions,
        });
      }
      const result = filter(value, suggestions, maximumDisplayingSuggestions);
      if (Array.isArray(result)) {
        this.setState({
          matches: result,
        });
      } else if (result.then && result.catch) {
        result.then(matches => {
          this.setState({
            matches,
          });
        });
      }
    },
    this.props.waitTime,
    {
      maxWait: 2000,
    },
  );

  handleChange = isInput => (arg0, arg1) => {
    const { onChange, selectOnly } = this.props;
    let newValue;
    if (arg0 && arg0.preventDefault && arg0.target) {
      if (arg1) {
        newValue = arg1;
      } else if (arg0.target.nodeName === 'INPUT' || arg0.target.nodeName === 'TEXTAREA') {
        newValue = arg0.target.value;
      }
    } else {
      newValue = arg0;
    }
    if (isInput && selectOnly) {
      this.setState({
        search: newValue,
      });
    } else {
      this.setState({
        value: newValue,
        search: newValue,
      });
      onChange && onChange(newValue);
    }
    this.getSuggestions(newValue);
  };

  handleSearch = (...args) => {
    const { onSearch } = this.props;
    this.handleChange(true)(...args);
    if (onSearch) {
      onSearch(...args);
    }
  };

  handleSelect = (...args) => {
    this.handleChange(false)(...args);
    const { onSelect } = this.props;
    if (onSelect) {
      onSelect(...args);
    }
  };

  handleBlur = (...args) => {
    this.setState(state => ({
      search: state.value,
    }));
  };

  render() {
    const {
      children,
      prefixCls,
      options,
      matchWidth,
      selectOnly,
      defaultValue,
      matchHighlight,
      fullWidth,
      placeholder,
      status,
      errorIcon,
      loadingIcon,
      successIcon,
      showStatusIcon,
      indent,
      ...other
    } = this.props;
    const { matches, search, isOpen } = this.state;
    const input = (
      <Input
        status={status}
        errorIcon={errorIcon}
        loadingIcon={loadingIcon}
        successIcon={successIcon}
        showStatusIcon={showStatusIcon}
        fullWidth={fullWidth}
        placeholder={placeholder}
        value={search}
        onChange={(...args) => {
          this.handleSearch(...args);
        }}
        autoComplete="off"/>
    );
    const menuStyle = matchWidth
      ? {
          width: `${this.width}px`,
        }
      : {};
    return (
      <div
        className={prefixCls}
        ref={this.getEl}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-haspopup={isOpen}>
        <DropdownMenu
          selectable={selectOnly}
          {...other}
          anchorEl={input}
          onSelect={this.handleSelect}
          onOpenChange={this.handleOpen}
          trigger="focus">
          <Menu
            className={classNames(`${prefixCls}-menu`)}
            role="listbox"
            style={menuStyle}
            indent={indent}>
            {options || matches.map(suggestion => this.suggestionsRender(suggestion, search))}
          </Menu>
        </DropdownMenu>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/autoComplete/AutoComplete.jsx